import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateMachine, useUpdateMachine } from '@/services/api/endpoints/MachineService';
import { useGetCompanies } from '@/services/api/endpoints/CompanyService';
import { MapPicker } from '@/components/shared/MapPicker';
import type { Machine } from '@/types';

interface MachineModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  machine: Machine | null;
}

interface FormState {
  companyId: string;
  deviceId: string;
  code: string;
  name: string;
  latitude: string;
  longitude: string;
  geofenceRadius: string;
  maxPrice: string;
  type: string;
  mdbRole: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>

const MACHINE_TYPES = [
  { value: 'esp32', label: 'ESP32' },
  { value: 'pax_im30', label: 'PAX IM30' },
  { value: 'pax_im25', label: 'PAX IM25' },
  { value: 'ciontek_cm30', label: 'Ciontek CM30' },
];

const MDB_ROLES = [
  { value: 'mdb_cashless_slave', label: 'MDB Cashless Slave' },
  { value: 'mdb_master', label: 'MDB Master' },
];

const initialFormState: FormState = {
  companyId: '',
  deviceId: '',
  code: '',
  name: '',
  latitude: '',
  longitude: '',
  geofenceRadius: '',
  maxPrice: '',
  type: '',
  mdbRole: '',
};

export function MachineModal({ open, onOpenChange, machine }: MachineModalProps) {
  const { t } = useTranslation();
  const isEditMode = !!machine;

  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});

  const { data: companiesData } = useGetCompanies(1, 100, '', 'name', 'asc');
  const createMachine = useCreateMachine();
  const updateMachine = useUpdateMachine();

  const isSubmitting = createMachine.isPending || updateMachine.isPending;

  useEffect(() => {
    if (open) {
      if (machine) {
        setForm({
          companyId: machine.companyId || '',
          deviceId: machine.deviceId || '',
          code: machine.code || '',
          name: machine.name || '',
          latitude: machine.latitude != null ? String(machine.latitude) : '',
          longitude: machine.longitude != null ? String(machine.longitude) : '',
          geofenceRadius: machine.geofenceRadius != null ? String(machine.geofenceRadius) : '',
          maxPrice: machine.maxPrice != null ? String(machine.maxPrice) : '',
          type: machine.type || '',
          mdbRole: machine.mdbRole || '',
        });
      } else {
        setForm(initialFormState);
      }
      setErrors({});
    }
  }, [open, machine]);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!form.companyId.trim()) {
      newErrors.companyId = t('machines.selectCompany');
    }
    if (!form.deviceId.trim()) {
      newErrors.deviceId = t('machines.deviceId');
    }
    if (!form.code.trim()) {
      newErrors.code = t('machines.code');
    }
    if (!form.name.trim()) {
      newErrors.name = t('machines.name');
    }
    if (form.latitude && isNaN(Number(form.latitude))) {
      newErrors.latitude = t('machines.latitude');
    }
    if (form.longitude && isNaN(Number(form.longitude))) {
      newErrors.longitude = t('machines.longitude');
    }
    if (form.geofenceRadius && isNaN(Number(form.geofenceRadius))) {
      newErrors.geofenceRadius = t('machines.geofenceRadius');
    }
    if (form.maxPrice && isNaN(Number(form.maxPrice))) {
      newErrors.maxPrice = t('machines.maxPrice');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const payload: Partial<Machine> = {
      companyId: form.companyId,
      name: form.name.trim(),
      ...(form.latitude ? { latitude: Number(form.latitude) } : {}),
      ...(form.longitude ? { longitude: Number(form.longitude) } : {}),
      ...(form.geofenceRadius ? { geofenceRadius: Number(form.geofenceRadius) } : {}),
      ...(form.maxPrice ? { maxPrice: Number(form.maxPrice) } : {}),
      ...(form.type ? { type: form.type as Machine['type'] } : {}),
      ...(form.mdbRole ? { mdbRole: form.mdbRole as Machine['mdbRole'] } : {}),
    };

    if (!isEditMode) {
      payload.deviceId = form.deviceId.trim();
      payload.code = form.code.trim();
    }

    try {
      if (isEditMode) {
        await updateMachine.mutateAsync({ id: machine.id, ...payload });
        toast.success(t('machines.updateSuccess'));
      } else {
        await createMachine.mutateAsync(payload);
        toast.success(t('machines.createSuccess'));
      }
      onOpenChange(false);
    } catch {
      toast.error(isEditMode ? t('machines.updateError') : t('machines.createError'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? t('machines.editMachine') : t('machines.createMachine')}
          </DialogTitle>
          <DialogDescription>
            {isEditMode ? t('machines.editMachine') : t('machines.createMachine')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Company Select */}
          <div className="space-y-2">
            <Label htmlFor="companyId">{t('machines.company')} *</Label>
            <Select value={form.companyId} onValueChange={(v) => handleChange('companyId', v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('machines.selectCompany')} />
              </SelectTrigger>
              <SelectContent>
                {companiesData?.items.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.companyId && <p className="text-sm text-destructive">{errors.companyId}</p>}
          </div>

          {/* Device ID */}
          <div className="space-y-2">
            <Label htmlFor="deviceId">{t('machines.deviceId')} *</Label>
            <Input
              id="deviceId"
              value={form.deviceId}
              onChange={(e) => handleChange('deviceId', e.target.value)}
              disabled={isEditMode}
              error={errors.deviceId}
            />
          </div>

          {/* Code */}
          <div className="space-y-2">
            <Label htmlFor="code">{t('machines.code')} *</Label>
            <Input
              id="code"
              value={form.code}
              onChange={(e) => handleChange('code', e.target.value)}
              disabled={isEditMode}
              error={errors.code}
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">{t('machines.name')} *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={errors.name}
            />
          </div>

          {/* Type / MDB Role row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('machines.type')}</Label>
              <Select value={form.type} onValueChange={(v) => handleChange('type', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('machines.selectType')} />
                </SelectTrigger>
                <SelectContent>
                  {MACHINE_TYPES.map((mt) => (
                    <SelectItem key={mt.value} value={mt.value}>
                      {mt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('machines.mdbRole')}</Label>
              <Select value={form.mdbRole} onValueChange={(v) => handleChange('mdbRole', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('machines.selectMdbRole')} />
                </SelectTrigger>
                <SelectContent>
                  {MDB_ROLES.map((mr) => (
                    <SelectItem key={mr.value} value={mr.value}>
                      {mr.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Map Picker */}
          <div className="space-y-2">
            <Label>{t('machines.location')}</Label>
            <MapPicker
              latitude={form.latitude ? Number(form.latitude) : undefined}
              longitude={form.longitude ? Number(form.longitude) : undefined}
              geofenceRadius={form.geofenceRadius ? Number(form.geofenceRadius) : 50}
              onLocationSelect={(lat, lng) => {
                handleChange('latitude', lat.toFixed(7));
                handleChange('longitude', lng.toFixed(7));
              }}
              disabled={isSubmitting}
              height="280px"
            />
          </div>

          {/* Latitude / Longitude / Geofence row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="latitude" className="text-xs">{t('machines.latitude')}</Label>
              <Input
                id="latitude"
                type="text"
                inputMode="decimal"
                value={form.latitude}
                onChange={(e) => handleChange('latitude', e.target.value)}
                error={errors.latitude}
                className="text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="longitude" className="text-xs">{t('machines.longitude')}</Label>
              <Input
                id="longitude"
                type="text"
                inputMode="decimal"
                value={form.longitude}
                onChange={(e) => handleChange('longitude', e.target.value)}
                error={errors.longitude}
                className="text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="geofenceRadius" className="text-xs">{t('machines.geofenceRadius')}</Label>
              <Input
                id="geofenceRadius"
                type="text"
                inputMode="numeric"
                value={form.geofenceRadius}
                onChange={(e) => handleChange('geofenceRadius', e.target.value)}
                error={errors.geofenceRadius}
                className="text-xs"
              />
            </div>
          </div>

          {/* Max Price */}
          <div className="space-y-2">
            <Label htmlFor="maxPrice">{t('machines.maxPrice')}</Label>
            <Input
              id="maxPrice"
              type="text"
              inputMode="decimal"
              value={form.maxPrice}
              onChange={(e) => handleChange('maxPrice', e.target.value)}
              error={errors.maxPrice}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {isEditMode ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
